import React, {useEffect, useState} from 'react';
import {Toast, ToastContainer} from 'react-bootstrap';
import {Icon, IconProps} from '@/components/icons/Icon';
import Button from '@/components/ui/Button';
import {ToastAlertProps} from './types';

const variantConfig = {
    success: {
        icon: 'mdi-check-circle',
        bg: 'success',
        headerBg: '#d1e7dd',
        borderColor: '#198754',
        iconColor: '#198754'
    },
    error: {
        icon: 'mdi-alert-circle',
        bg: 'danger',
        headerBg: '#f8d7da',
        borderColor: '#dc3545',
        iconColor: '#dc3545'
    },
    warning: {
        icon: 'mdi-alert',
        bg: 'warning',
        headerBg: '#fff3cd',
        borderColor: '#ffc107',
        iconColor: '#f57c00'
    },
    info: {
        icon: 'mdi-information',
        bg: 'info',
        headerBg: '#d1ecf1',
        borderColor: '#0dcaf0',
        iconColor: '#0dcaf0'
    }
};

interface ToastAlertWithIdProps extends ToastAlertProps {
  id: string;
  onClose: () => void;
}

const ToastAlert: React.FC<ToastAlertWithIdProps> = ({
    id,
    variant,
    title,
    message,
    duration = 5000,
    persistent = false,
    dismissible = true,
    onDismiss,
    onClose,
    className = '',
    icon,
    actions = []
}) => {
    const [show, setShow] = useState(true);

    useEffect(() => {
        if (!persistent && duration > 0) {
            const timer = setTimeout(() => {
                handleClose();
            }, duration);

            return () => clearTimeout(timer);
        }
    }, [duration, persistent]);

    const handleClose = () => {
        setShow(false);
        onClose();
        onDismiss?.();
    };

    const config = variantConfig[variant];
    const toastIcon = icon || config.icon;

    return (
        <Toast
            show={show}
            onClose={dismissible ? handleClose : undefined}
            className={`shadow-lg border-0 ${className}`.trim()}
            bg={config.bg}
            style={{
                borderLeft: `4px solid ${config.borderColor}`,
                borderRadius: '8px',
                minWidth: '320px',
                maxWidth: '400px'
            }}
        >
            <Toast.Header
                closeButton={false}
                style={{
                    backgroundColor: config.headerBg,
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 40px 12px 16px',
                    borderBottom: `1px solid ${config.borderColor}20`,
                    position: 'relative'
                }}
            >
                {toastIcon && (
                    <Icon
                        name={toastIcon as IconProps['name']}
                        className="mr-2"
                        style={{
                            fontSize: '1.2rem',
                            color: config.iconColor
                        }}
                    />
                )}
                <strong className="mr-auto" style={{fontSize: '0.95rem', fontWeight: 600}}>
                    {title || variant.charAt(0).toUpperCase() + variant.slice(1)}
                </strong>
                {dismissible && (
                    <button
                        type="button"
                        className="btn-close-custom"
                        onClick={handleClose}
                        data-cy="toast-alert-close-btn"
                        style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: 'none',
                            border: 'none',
                            padding: '4px',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0.7,
                            transition: 'opacity 0.2s ease, background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '0.7';
                            e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        aria-label="Close"
                    >
                        <Icon
                            name="mdi-close"
                            style={{
                                fontSize: '1.1rem',
                                color: '#6c757d'
                            }}
                        />
                    </button>
                )}
            </Toast.Header>
            <Toast.Body
                className="text-white"
                style={{
                    padding: '16px',
                    fontSize: '0.9rem',
                    lineHeight: '1.4'
                }}
            >
                <div className="mb-0">{message}</div>
                {actions.length > 0 && (
                    <div className="mt-3 d-flex gap-2">
                        {actions.map((action, index) => (
                            <Button
                                key={index}
                                variant={action.variant || 'light'}
                                size="xs"
                                onClick={action.onClick}
                                loading={action.loading}
                                style={{
                                    fontSize: '0.8rem',
                                    padding: '4px 12px',
                                    borderRadius: '4px'
                                }}
                                data-cy="toast-alert-btn">
                                {action.label}
                            </Button>
                        ))}
                    </div>
                )}
            </Toast.Body>
        </Toast>
    );
};

export {ToastAlert};

interface ToastContainerWrapperProps {
  position?: ToastAlertProps['position'];
  children: React.ReactNode;
}

export const ToastContainerWrapper: React.FC<ToastContainerWrapperProps> = ({
    position = 'top-center',
    children
}) => {
    const isCenter = position?.includes('center');
    const isTop = position?.startsWith('top');
    const isBottom = position?.startsWith('bottom');
    const isRight = position?.includes('right');
    const isLeft = position?.includes('left');

    const getPositionClass = () => {
        switch (position) {
            case 'top-center': return 'start-50 translate-middle-x';
            case 'bottom-center': return 'start-50 translate-middle-x';
            default: return '';
        }
    };

    const getContainerStyle = (): React.CSSProperties => {
        const baseStyle: React.CSSProperties = {
            position: 'fixed',
            zIndex: 9999,
            pointerEvents: 'none'
        };

        if (isCenter) {
            return {
                ...baseStyle,
                top: isTop ? '80px' : 'auto',
                bottom: isBottom ? '20px' : 'auto',
                left: '50%',
                right: 'auto',
                transform: 'translateX(-50%)'
            };
        }

        return {
            ...baseStyle,
            top: isTop ? '80px' : 'auto',
            bottom: isBottom ? '20px' : 'auto',
            right: isRight ? '20px' : 'auto',
            left: isLeft ? '20px' : 'auto'
        };
    };

    return (
        <ToastContainer
            className={`p-3 ${getPositionClass()}`}
            style={getContainerStyle()}
        >
            <style>
                {`
          .toast {
            pointer-events: auto;
            transition: all 0.3s ease-in-out;
            opacity: 1;
          }
          
          .toast.show {
            animation: ${isCenter && isTop ? 'slideInFromTop' : isCenter && isBottom ? 'slideInFromBottom' : isRight ? 'slideInFromRight' : 'slideInFromLeft'} 0.3s ease-out;
          }
          
          .toast.hide {
            animation: ${isCenter && isTop ? 'slideOutToTop' : isCenter && isBottom ? 'slideOutToBottom' : isRight ? 'slideOutToRight' : 'slideOutToLeft'} 0.3s ease-in;
          }
          
          @keyframes slideInFromTop {
            from {
              transform: translateY(-100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          @keyframes slideOutToTop {
            from {
              transform: translateY(0);
              opacity: 1;
            }
            to {
              transform: translateY(-100%);
              opacity: 0;
            }
          }
          
          @keyframes slideInFromBottom {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          @keyframes slideOutToBottom {
            from {
              transform: translateY(0);
              opacity: 1;
            }
            to {
              transform: translateY(100%);
              opacity: 0;
            }
          }
          
          @keyframes slideInFromRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes slideOutToRight {
            from {
              transform: translateX(0);
              opacity: 1;
            }
            to {
              transform: translateX(100%);
              opacity: 0;
            }
          }
          
          @keyframes slideInFromLeft {
            from {
              transform: translateX(-100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes slideOutToLeft {
            from {
              transform: translateX(0);
              opacity: 1;
            }
            to {
              transform: translateX(-100%);
              opacity: 0;
            }
          }
          
          .toast:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
          }
          
          .toast .toast-header {
            border-bottom: none;
            position: relative;
          }
          
          .toast .btn-close-custom {
            z-index: 1;
          }
          
          .toast .btn-close-custom:focus {
            outline: 2px solid rgba(0, 123, 255, 0.5);
            outline-offset: 2px;
          }
        `}
            </style>
            {children}
        </ToastContainer>
    );
};
